import { Pin, X, MessageSquare } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface PinnedCardProps {
  question: string
  answer: string
  pinnedAt: string
  onUnpin: () => void
}

const MARKDOWN_COMPONENTS: Parameters<typeof ReactMarkdown>[0]['components'] = {
  p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full text-xs border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-slate-200">{children}</thead>
  ),
  th: ({ children, style }) => (
    <th className="px-2 py-1 text-left text-xs font-semibold text-ink-soft" style={style}>
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td className="px-2 py-1 border-t border-slate-100 text-xs" style={style}>
      {children}
    </td>
  ),
  tr: ({ children }) => <tr className="odd:bg-slate-50/50">{children}</tr>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-0.5 my-1">{children}</ol>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-0.5 my-1">{children}</ul>,
  li: ({ children }) => <li className="text-xs leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  h1: ({ children }) => <h1 className="text-sm font-bold mt-1 mb-0.5">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xs font-bold mt-1 mb-0.5">{children}</h2>,
  h3: ({ children }) => <h3 className="text-xs font-semibold mt-0.5 mb-0.5">{children}</h3>,
}

export function PinnedCard({ question, answer, pinnedAt, onUnpin }: PinnedCardProps) {
  const time = new Date(pinnedAt)
  const dateStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return (
    <div className="mm-card-v3 h-full flex flex-col overflow-hidden group">
      {/* Header */}
      <div className="flex items-start gap-2 px-4 pt-3 pb-2 border-b border-slate-100">
        <Pin className="h-3.5 w-3.5 text-butler-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-ink-dark leading-snug line-clamp-2">
            {question}
          </p>
          <p className="text-[10px] text-ink-soft mt-0.5">{dateStr}</p>
        </div>
        <button
          type="button"
          onClick={onUnpin}
          aria-label="Unpin card"
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 -mt-0.5 -mr-1 rounded hover:bg-red-50 text-ink-soft hover:text-red-500"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Answer content */}
      <div className="flex-1 overflow-y-auto px-4 py-3 text-sm text-ink-dark leading-relaxed">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={MARKDOWN_COMPONENTS}
          skipHtml
          disallowedElements={['img', 'script', 'iframe']}
        >
          {answer}
        </ReactMarkdown>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-1.5 px-4 py-2 border-t border-slate-100 bg-slate-50/50">
        <MessageSquare className="h-3 w-3 text-ink-soft" />
        <span className="text-[10px] text-ink-soft">From chat</span>
      </div>
    </div>
  )
}
